import { useState } from "react";
import { Layers } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { buildRowLabel } from "@/lib/table-utils";

interface MergeRowsDialogProps {
  onClose: () => void;
  mode: "create" | "edit";
  initialName: string;
  initialDirections?: string;
  /** The rows being merged, in table order. */
  rows: string[][];
  onSubmit: (values: { name: string; directions: string }) => Promise<void>;
}

/**
 * Names a merge (and optional directions for the generator) when creating or editing it. Mount it
 * only while open: the fields start from the initial values.
 */
export function MergeRowsDialog({
  onClose,
  mode,
  initialName,
  initialDirections = "",
  rows,
  onSubmit,
}: MergeRowsDialogProps) {
  const [name, setName] = useState(initialName);
  const [directions, setDirections] = useState(initialDirections);
  const [isSaving, setIsSaving] = useState(false);

  const trimmedName = name.trim();

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!trimmedName) return;
    setIsSaving(true);
    try {
      await onSubmit({ name: trimmedName, directions: directions.trim() });
      onClose();
    } catch {
      // The action hook already reported the error; keep the dialog open to retry.
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <form onSubmit={(event) => void handleSubmit(event)} className="space-y-4">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? `Merge ${rows.length} Rows` : "Edit Merged Rows"}
            </DialogTitle>
            <DialogDescription>
              Merged rows become one submittal line item when you generate.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            <Label htmlFor="merge-name">Merge Name</Label>
            <Input
              id="merge-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Exit & Emergency Lighting"
              aria-invalid={trimmedName ? undefined : true}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="merge-directions">Directions (Optional)</Label>
            <Textarea
              id="merge-directions"
              value={directions}
              onChange={(event) => setDirections(event.target.value)}
              placeholder="Focus on compliance notes and warranty requirements."
              className="min-h-20"
            />
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Rows</p>
            <ul className="max-h-48 space-y-1 overflow-y-auto rounded-md border p-1.5 text-sm">
              {rows.map((row, index) => (
                <li key={index} className="truncate rounded-sm bg-muted/50 px-2 py-1.5">
                  {buildRowLabel(row)}
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving || !trimmedName}>
              {mode === "create" ? <Layers /> : null}
              {isSaving ? "Saving..." : mode === "create" ? "Merge Rows" : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
